import { create } from 'zustand'
import { apiClient } from '../lib/api'
import { track } from '../lib/analytics'
import type { Thread, PaginatedResponse, SystemPromptPreset } from '../lib/types'

interface ThreadState {
  threads: Thread[]
  trashedThreads: Thread[]
  presets: SystemPromptPreset[]
  loading: boolean
  error: string | null
  nextCursor: string | null

  fetchThreads: () => Promise<void>
  fetchMore: () => Promise<void>
  createThread: (data: { ai_model?: string; system_prompt_preset?: number }) => Promise<Thread>
  updateThread: (id: number, data: { title: string }) => Promise<void>
  deleteThread: (id: number) => Promise<void>
  pinThread: (id: number) => Promise<void>
  restoreThread: (id: number) => Promise<void>
  permanentDeleteThread: (id: number) => Promise<void>
  fetchTrash: () => Promise<void>
  fetchPresets: () => Promise<void>
  createPreset: (data: { name: string; content: string }) => Promise<SystemPromptPreset>
  updatePreset: (id: number, data: { name?: string; content?: string }) => Promise<void>
  deletePreset: (id: number) => Promise<void>
  triggerDocSync: (id: number) => Promise<void>
  renameThread: (id: number) => Promise<string>
  setThreadModel: (id: number, model: string) => Promise<void>
  setThreadPreset: (id: number, presetId: number | null) => Promise<void>
  fetchModels: () => Promise<{ id: string; context_window: number }[]>
}

export const useThreadStore = create<ThreadState>((set, get) => ({
  threads: [],
  trashedThreads: [],
  presets: [],
  loading: false,
  error: null,
  nextCursor: null,

  fetchThreads: async () => {
    set({ loading: true, error: null })
    try {
      const data = await apiClient.get<PaginatedResponse<Thread>>('research/threads/')
      set({ threads: data.results, nextCursor: data.next })
    } catch {
      set({ error: 'Failed to load threads.' })
    } finally {
      set({ loading: false })
    }
  },

  fetchMore: async () => {
    const { nextCursor } = get()
    if (!nextCursor) return
    const url = nextCursor.replace(/^.*\/api\/v1\//, '')
    const data = await apiClient.get<PaginatedResponse<Thread>>(url)
    set((s) => ({ threads: [...s.threads, ...data.results], nextCursor: data.next }))
  },

  createThread: async (data) => {
    try {
      const thread = await apiClient.post<Thread>('research/threads/', data)
      set((s) => ({ threads: [thread, ...s.threads] }))
      track('thread_created', { model: thread.ai_model, has_preset: !!data.system_prompt_preset, preset_id: data.system_prompt_preset })
      try {
        if (!localStorage.getItem('scrivaro.activation.first_thread')) {
          localStorage.setItem('scrivaro.activation.first_thread', String(Date.now()))
          track('activation_first_thread', { model: thread.ai_model })
        }
      } catch { /* localStorage unavailable */ }
      return thread
    } catch (err) {
      set({ error: 'Failed to create thread.' })
      throw err
    }
  },

  updateThread: async (id, data) => {
    const old = get().threads.find((t) => t.id === id)
    set((s) => ({ threads: s.threads.map((t) => (t.id === id ? { ...t, ...data } : t)) }))
    try {
      const updated = await apiClient.patch<Thread>(`research/threads/${id}/`, data)
      // Merge — ThreadUpdateSerializer returns partial fields (e.g. just title)
      set((s) => ({ threads: s.threads.map((t) => (t.id === id ? { ...t, ...updated } : t)) }))
    } catch {
      if (old) set((s) => ({ threads: s.threads.map((t) => (t.id === id ? old : t)) }))
    }
  },

  deleteThread: async (id) => {
    await apiClient.delete(`research/threads/${id}/`)
    set((s) => ({ threads: s.threads.filter((t) => t.id !== id) }))
    track('thread_deleted', { thread_id: id })
  },

  pinThread: async (id) => {
    const updated = await apiClient.post<Thread>(`research/threads/${id}/pin/`)
    set((s) => {
      const threads = s.threads.map((t) => (t.id === id ? updated : t))
      threads.sort((a, b) => {
        if (a.pinned !== b.pinned) return a.pinned ? -1 : 1
        return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      })
      return { threads }
    })
  },

  restoreThread: async (id) => {
    try {
      const restored = await apiClient.post<Thread>(`research/threads/${id}/restore/`)
      set((s) => ({
        trashedThreads: s.trashedThreads.filter((t) => t.id !== id),
        threads: [restored, ...s.threads],
      }))
    } catch {
      set({ error: 'Failed to restore thread.' })
    }
  },

  permanentDeleteThread: async (id) => {
    const old = get().trashedThreads
    set((s) => ({ trashedThreads: s.trashedThreads.filter((t) => t.id !== id) }))
    try {
      await apiClient.delete(`research/threads/${id}/permanent/`)
    } catch {
      set({ trashedThreads: old, error: 'Failed to delete thread.' })
    }
  },

  fetchTrash: async () => {
    try {
      const data = await apiClient.get<PaginatedResponse<Thread>>('research/threads/trash/')
      set({ trashedThreads: data.results, error: null })
    } catch {
      set({ error: 'Failed to load trash.' })
    }
  },

  fetchPresets: async () => {
    const data = await apiClient.get<PaginatedResponse<SystemPromptPreset>>('research/presets/')
    set({ presets: data.results })
  },

  createPreset: async (data) => {
    const preset = await apiClient.post<SystemPromptPreset>('research/presets/', data)
    set((s) => ({ presets: [preset, ...s.presets] }))
    return preset
  },

  updatePreset: async (id, data) => {
    const updated = await apiClient.patch<SystemPromptPreset>(`research/presets/${id}/`, data)
    set((s) => ({ presets: s.presets.map((p) => (p.id === id ? updated : p)) }))
  },

  deletePreset: async (id) => {
    await apiClient.delete(`research/presets/${id}/`)
    set((s) => ({ presets: s.presets.filter((p) => p.id !== id) }))
  },

  triggerDocSync: async (id) => {
    await apiClient.post(`research/threads/${id}/doc/sync/`)
    track('docs_sync_triggered', { thread_id: id })
  },

  renameThread: async (id) => {
    const data = await apiClient.post<{ title: string }>(`research/threads/${id}/rename/`)
    set((s) => ({ threads: s.threads.map((t) => (t.id === id ? { ...t, title: data.title } : t)) }))
    return data.title
  },

  setThreadModel: async (id, model) => {
    const old = get().threads.find((t) => t.id === id)
    set((s) => ({ threads: s.threads.map((t) => (t.id === id ? { ...t, ai_model: model } : t)) }))
    try {
      const updated = await apiClient.post<Thread>(`research/threads/${id}/model/`, { ai_model: model })
      set((s) => ({ threads: s.threads.map((t) => (t.id === id ? updated : t)) }))
    } catch {
      if (old) set((s) => ({ threads: s.threads.map((t) => (t.id === id ? old : t)) }))
    }
  },

  setThreadPreset: async (id, presetId) => {
    const old = get().threads.find((t) => t.id === id)
    try {
      const updated = await apiClient.post<Thread>(`research/threads/${id}/preset/`, {
        system_prompt_preset: presetId,
      })
      set((s) => ({ threads: s.threads.map((t) => (t.id === id ? { ...t, ...updated } : t)) }))
    } catch {
      if (old) set((s) => ({ threads: s.threads.map((t) => (t.id === id ? old : t)) }))
    }
  },

  fetchModels: async () => {
    return apiClient.get<{ id: string; context_window: number }[]>('research/threads/models/')
  },
}))
